import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import styles from './OpenAITranslateTool.module.css';

const DEFAULT_MODEL = 'gpt-5-nano';
const DEFAULT_CHUNK_SIZE = 20;
const DEFAULT_MAX_CONCURRENT_REQUESTS = 100;
const POLL_INTERVAL_MS = 6500;

function OpenAITranslateTool() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('English');
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [chunkSize, setChunkSize] = useState(DEFAULT_CHUNK_SIZE);
  const [maxConcurrentRequests, setMaxConcurrentRequests] = useState(DEFAULT_MAX_CONCURRENT_REQUESTS);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [completedChunks, setCompletedChunks] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [totalSentences, setTotalSentences] = useState(0);
  const [translatedCount, setTranslatedCount] = useState(0);
  const [promptTokens, setPromptTokens] = useState(null);
  const [completionTokens, setCompletionTokens] = useState(null);
  const [taskId, setTaskId] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [volumes, setVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [volumesError, setVolumesError] = useState('');
  const [selectedVolumeId, setSelectedVolumeId] = useState('');
  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [chaptersError, setChaptersError] = useState('');
  const [selectedChapterIds, setSelectedChapterIds] = useState([]);
  const [batchError, setBatchError] = useState('');
  const [batchResult, setBatchResult] = useState(null);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [batchTaskId, setBatchTaskId] = useState('');
  const [batchStatus, setBatchStatus] = useState('idle');
  const [batchProgress, setBatchProgress] = useState({
    totalChapters: 0,
    processedChapters: 0,
    succeededChapters: 0,
    failedChapters: 0,
    currentChapterTitle: '',
    totalSentences: 0,
    translatedSentences: 0,
    progress: 0,
  });
  const pollTimerRef = useRef(null);
  const batchPollTimerRef = useRef(null);
  const [reviewSelectedChapterId, setReviewSelectedChapterId] = useState(null);
  const [reviewSelectedChapterTitle, setReviewSelectedChapterTitle] = useState('');
  const [reviewSentences, setReviewSentences] = useState([]);
  const [reviewSentencesLoading, setReviewSentencesLoading] = useState(false);
  const [reviewSentencesError, setReviewSentencesError] = useState('');
  const [reviewInstructions, setReviewInstructions] = useState('');
  const [sentenceFilter, setSentenceFilter] = useState('');
  const [editingSentenceKey, setEditingSentenceKey] = useState(null);
  const [directChapterIdInput, setDirectChapterIdInput] = useState('');
  const [chineseDetectMode, setChineseDetectMode] = useState(false);

  // 检测字符是否为中文（CJK 统一汉字 + 扩展 A）
  const isChinese = (char) => /[\u4e00-\u9fff\u3400-\u4dbf]/.test(char);

  const backendBaseUrl = useMemo(() => {
    const baseUrl = api.defaults.baseURL || '';
    if (!baseUrl) {
      return '';
    }
    const trimmed = baseUrl.replace(/\/?$/, '');
    return trimmed.replace(/\/api$/, '');
  }, []);

  const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes)) {
      return '';
    }
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${bytes} B`;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    setSelectedFile(file);
    setResult(null);
    setError('');
    setCopyStatus('');
    setStatus('idle');
    setProgress(0);
    setTaskId('');
    setIsProcessing(false);
    setPromptTokens(null);
    setCompletionTokens(null);
    if (file && file.size === 0) {
      setError('文件为空，请选择有效的 JSON 文件');
    }
  };

  const toggleChapterSelection = (chapterId) => {
    setBatchError('');
    setBatchResult(null);
    setSelectedChapterIds((prev) => {
      if (prev.includes(chapterId)) {
        return prev.filter((id) => id !== chapterId);
      }
      return [...prev, chapterId];
    });
  };

  const toggleSelectAllChapters = () => {
    if (chapters.length === 0) {
      return;
    }
    setBatchError('');
    setBatchResult(null);
    const allIds = chapters.map((chapter) => chapter.id);
    const hasAll = allIds.every((id) => selectedChapterIds.includes(id));
    if (hasAll) {
      setSelectedChapterIds([]);
    } else {
      setSelectedChapterIds(allIds);
    }
  };

  const loadVolumes = async () => {
    setVolumesLoading(true);
    setVolumesError('');
    setBatchError('');
    setBatchResult(null);
    try {
      const { data } = await api.get('/volumes/', { params: { limit: 500 } });
      const list = Array.isArray(data) ? data : [];
      setVolumes(list);
      setSelectedVolumeId((prev) => {
        if (prev && list.some((item) => String(item.id) === String(prev))) {
          return prev;
        }
        return list.length > 0 ? String(list[0].id) : '';
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '获取卷列表失败，请稍后重试';
      setVolumesError(message);
    } finally {
      setVolumesLoading(false);
    }
  };

  const loadChapters = async (volumeId) => {
    if (!volumeId) {
      setChapters([]);
      setChaptersError('');
      setBatchError('');
      setBatchResult(null);
      return;
    }
    setChaptersLoading(true);
    setChaptersError('');
    setSelectedChapterIds([]);
    setBatchError('');
    setBatchResult(null);
    try {
      const { data } = await api.get('/chapters/', {
        params: { limit: 500, volume_id: volumeId, lite: true },
      });
      const list = Array.isArray(data) ? data : [];
      setChapters(list);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '获取章节列表失败，请稍后重试';
      setChaptersError(message);
    } finally {
      setChaptersLoading(false);
    }
  };

  useEffect(() => {
    loadVolumes();
  }, []);

  useEffect(() => {
    loadChapters(selectedVolumeId);
  }, [selectedVolumeId]);

  const fetchReviewSentences = useCallback(async (chapterId) => {
    if (!chapterId) {
      setReviewSentences([]);
      setReviewSentencesError('');
      setReviewSelectedChapterTitle('');
      setEditingSentenceKey(null);
      return;
    }
    setReviewSentencesLoading(true);
    setReviewSentencesError('');
    setEditingSentenceKey(null);
    try {
      const { data } = await api.get(`/openai-translate/chapters/${chapterId}/sentences`);
      setReviewSelectedChapterTitle(data?.title || '');
      const list = Array.isArray(data?.sentences) ? data.sentences : [];
      const normalized = list.map((item) => ({
        ...item,
        draft: item.translated_text ?? '',
        isRetranslating: false,
        isSaving: false,
        actionError: '',
        actionSuccess: '',
      }));
      setReviewSentences(normalized);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '加载句子列表失败，请稍后重试';
      setReviewSentencesError(message);
      setReviewSentences([]);
    } finally {
      setReviewSentencesLoading(false);
    }
  }, []);

  const updateReviewSentence = useCallback((sentenceKey, updates) => {
    setReviewSentences((prev) =>
      prev.map((item) => (item.key === sentenceKey ? { ...item, ...updates } : item)),
    );
  }, []);

  const handleSentenceDraftChange = (sentenceKey, value) => {
    setEditingSentenceKey(sentenceKey);
    updateReviewSentence(sentenceKey, {
      draft: value,
      actionError: '',
      actionSuccess: '',
    });
  };

  const handleRetranslateSentence = async (sentenceKey) => {
    if (!reviewSelectedChapterId) {
      return;
    }
    const sentence = reviewSentences.find((item) => item.key === sentenceKey);
    if (!sentence) {
      return;
    }
    updateReviewSentence(sentenceKey, {
      isRetranslating: true,
      actionError: '',
      actionSuccess: '',
    });
    try {
      const { data } = await api.post(
        `/openai-translate/chapters/${reviewSelectedChapterId}/sentences/${encodeURIComponent(sentenceKey)}/retranslate`,
        {
          target_language: targetLanguage.trim() || 'English',
          model: model.trim() || DEFAULT_MODEL,
          instructions: reviewInstructions.trim() || undefined,
        },
      );
      const nextTranslation = data?.translation ?? '';
      updateReviewSentence(sentenceKey, {
        draft: nextTranslation,
        isRetranslating: false,
        actionError: '',
        actionSuccess: '已生成新的翻译建议',
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '重新翻译失败，请稍后重试';
      updateReviewSentence(sentenceKey, {
        isRetranslating: false,
        actionError: message,
        actionSuccess: '',
      });
    }
  };

  const handleSaveSentenceTranslation = async (sentenceKey) => {
    if (!reviewSelectedChapterId) {
      return;
    }
    const sentence = reviewSentences.find((item) => item.key === sentenceKey);
    if (!sentence) {
      return;
    }
    const trimmed = sentence.draft?.trim();
    if (!trimmed) {
      updateReviewSentence(sentenceKey, {
        actionError: '翻译内容不能为空',
        actionSuccess: '',
      });
      return;
    }

    updateReviewSentence(sentenceKey, {
      isSaving: true,
      actionError: '',
      actionSuccess: '',
    });
    try {
      await api.put(
        `/openai-translate/chapters/${reviewSelectedChapterId}/sentences/${encodeURIComponent(sentenceKey)}`,
        { translation: trimmed },
      );
      updateReviewSentence(sentenceKey, {
        translated_text: trimmed,
        draft: trimmed,
        isSaving: false,
        actionSuccess: '已保存新的翻译',
        actionError: '',
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '保存翻译失败，请稍后重试';
      updateReviewSentence(sentenceKey, {
        isSaving: false,
        actionError: message,
        actionSuccess: '',
      });
    }
  };

  const handleDirectChapterLoad = (detectMode = false) => {
    const trimmed = directChapterIdInput.trim();
    if (!trimmed) return;
    const parsed = parseInt(trimmed, 10);
    if (Number.isNaN(parsed) || parsed <= 0) return;
    setSentenceFilter('');
    setEditingSentenceKey(null);
    setChineseDetectMode(detectMode);
    setReviewSelectedChapterId(parsed);
    fetchReviewSentences(parsed);
  };

  const filteredReviewSentences = useMemo(() => {
    let base = reviewSentences;

    // 中文检测模式：只保留 translated_text 前两个字符中任意一个为中文的句子
    if (chineseDetectMode) {
      base = base.filter((item) => {
        const text = (item.translated_text ?? '').trim();
        if (text.length === 0) return false;
        return isChinese(text.charAt(0)) || (text.length > 1 && isChinese(text.charAt(1)));
      });
    }

    if (!sentenceFilter.trim()) {
      return base;
    }
    const keyword = sentenceFilter.trim().toLowerCase();
    return base.filter((item) => {
      const original = item.original_text?.toLowerCase() ?? '';
      const draft = item.draft?.toLowerCase() ?? '';
      const saved = item.translated_text?.toLowerCase() ?? '';
      const matches =
        original.includes(keyword) ||
        draft.includes(keyword) ||
        saved.includes(keyword) ||
        String(item.index + 1).includes(keyword) ||
        (item.sentence_id != null && String(item.sentence_id).includes(keyword));
      if (matches) {
        return true;
      }
      return item.key === editingSentenceKey;
    });
  }, [reviewSentences, sentenceFilter, editingSentenceKey, chineseDetectMode]);

  // reviewSelectedChapterId 变化时不再自动拉取，由 handleDirectChapterLoad 主动触发

  const cleanupPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const cleanupBatchPolling = () => {
    if (batchPollTimerRef.current) {
      clearInterval(batchPollTimerRef.current);
      batchPollTimerRef.current = null;
    }
  };

  useEffect(() => () => {
    cleanupPolling();
    cleanupBatchPolling();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setCopyStatus('');
    setResult(null);
    cleanupPolling();

    if (!selectedFile) {
      setError('请选择要上传的 JSON 文件');
      return;
    }
    if (selectedFile.size === 0) {
      setError('文件为空，请重新上传 JSON 文件');
      return;
    }

    const sanitizedConcurrency = Number.isFinite(maxConcurrentRequests)
      ? Math.min(500, Math.max(1, Math.round(maxConcurrentRequests)))
      : DEFAULT_MAX_CONCURRENT_REQUESTS;

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('target_language', targetLanguage.trim() || 'English');
    formData.append('model', model.trim() || DEFAULT_MODEL);
    formData.append('chunk_size', String(chunkSize || DEFAULT_CHUNK_SIZE));
    formData.append(
      'max_concurrent_requests',
      String(sanitizedConcurrency),
    );
    if (instructions.trim()) {
      formData.append('instructions', instructions.trim());
    }

    try {
      setIsUploading(true);
      const { data } = await api.post('/openai-translate/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setTaskId(data.task_id);
      setStatus(data.status || 'queued');
      setProgress(0);
      setCompletedChunks(0);
      setTotalChunks(data.total_chunks || 0);
      setTotalSentences(data.total_sentences || 0);
      setTranslatedCount(0);
      setPromptTokens(null);
      setCompletionTokens(null);
      setIsProcessing(true);
      setCopyStatus('');
    } catch (err) {
      if (err?.code === 'ECONNABORTED') {
        setError('翻译失败：请求超时，请稍后重试或拆分文件。');
        return;
      }

      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '翻译失败，请稍后重试';
      setError(`翻译失败：${message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleBatchSubmit = async (event) => {
    event.preventDefault();
    setBatchError('');
    setBatchResult(null);
    cleanupBatchPolling();

    if (!selectedVolumeId) {
      setBatchError('请选择要翻译的卷');
      return;
    }

    if (selectedChapterIds.length === 0) {
      setBatchError('请至少选择一个章节');
      return;
    }

    const chunkSizeValue = Number(chunkSize) || DEFAULT_CHUNK_SIZE;
    const rawConcurrency = Number(maxConcurrentRequests);
    const concurrencyValue = Number.isFinite(rawConcurrency)
      ? Math.min(500, Math.max(1, Math.round(rawConcurrency)))
      : DEFAULT_MAX_CONCURRENT_REQUESTS;

    const payload = {
      chapter_ids: selectedChapterIds,
      target_language: targetLanguage.trim() || 'English',
      model: model.trim() || DEFAULT_MODEL,
      chunk_size: chunkSizeValue,
      max_concurrent_requests: concurrencyValue,
    };
    if (instructions.trim()) {
      payload.instructions = instructions.trim();
    }

    try {
      setIsBatchProcessing(true);
      console.log('[批量翻译] 发送 POST 请求:', payload);
      const { data } = await api.post('/openai-translate/batch', payload, {
        timeout: 60000,
      });
      console.log('[批量翻译] POST 响应:', data);
      
      // 获取 task_id 并开始轮询
      if (data?.task_id) {
        setBatchTaskId(data.task_id);
        setBatchStatus(data.status || 'queued');
        setBatchProgress({
          totalChapters: data.total_chapters || 0,
          processedChapters: 0,
          succeededChapters: 0,
          failedChapters: 0,
          currentChapterTitle: '',
          totalSentences: 0,
          translatedSentences: 0,
          progress: 0,
        });
      } else {
        setBatchError('未能启动批量翻译任务');
        setIsBatchProcessing(false);
      }
    } catch (err) {
      if (err?.code === 'ECONNABORTED') {
        setBatchError('批量翻译请求超时，请稍后重试或减少章节数量');
      } else {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          '批量翻译失败，请稍后重试';
        setBatchError(message);
      }
      setIsBatchProcessing(false);
    }
  };

  useEffect(() => {
    if (!taskId) {
      return undefined;
    }

    let isCancelled = false;

    const fetchStatus = async () => {
      try {
        const { data } = await api.get(`/openai-translate/status/${taskId}`, {
          timeout: 30000,
        });

        if (isCancelled) {
          return;
        }

        setStatus(data.status);
        setProgress(data.progress || 0);
        setCompletedChunks(data.completed_chunks || 0);
        setTotalChunks(data.total_chunks || 0);
        setTotalSentences(data.total_sentences || 0);
        setTranslatedCount(data.translated_sentence_count || 0);
        setPromptTokens(data.prompt_tokens ?? null);
        setCompletionTokens(data.completion_tokens ?? null);

        if (data.status === 'completed') {
          cleanupPolling();
          setIsProcessing(false);
          if (data.saved_url || data.translated_json) {
            setResult({
              saved_url: data.saved_url || '',
              saved_path: data.saved_path || '',
              model: data.model,
              prompt_tokens: data.prompt_tokens,
              completion_tokens: data.completion_tokens,
              target_language: data.target_language,
              max_concurrent_requests: data.max_concurrent_requests,
            });
            setCopyStatus('');
          } else {
            setError('翻译完成，但未返回结果，请稍后重试。');
          }
        } else if (data.status === 'failed') {
          cleanupPolling();
          setIsProcessing(false);
          setError(`翻译失败：${data.error || '未知错误'}`);
        }
      } catch (err) {
        if (isCancelled) {
          return;
        }

        cleanupPolling();
        setIsProcessing(false);
        if (err?.response?.status === 404) {
          setError('翻译任务不存在或已过期');
        } else if (err?.code === 'ECONNABORTED') {
          setError('获取翻译进度超时，请稍后重试');
        } else {
          const message =
            err?.response?.data?.detail ||
            err?.message ||
            '获取翻译进度失败';
          setError(`获取翻译进度失败：${message}`);
        }
      }
    };

    fetchStatus();
    pollTimerRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      cleanupPolling();
    };
  }, [taskId]);

  useEffect(() => {
    if (!batchTaskId) {
      return undefined;
    }

    let isCancelled = false;

    const fetchBatchStatus = async () => {
      try {
        const { data } = await api.get(`/openai-translate/batch/status/${batchTaskId}`, {
          timeout: 30000,
        });

        if (isCancelled) {
          return;
        }

        console.log('[批量翻译进度]', {
          status: data.status,
          total_chapters: data.total_chapters,
          processed_chapters: data.processed_chapters,
          total_sentences: data.total_sentences,
          translated_sentences: data.translated_sentences,
          progress: data.progress,
        });

        setBatchStatus(data.status);
        setBatchProgress({
          totalChapters: data.total_chapters || 0,
          processedChapters: data.processed_chapters || 0,
          succeededChapters: data.succeeded_chapters || 0,
          failedChapters: data.failed_chapters || 0,
          currentChapterTitle: data.current_chapter_title || '',
          totalSentences: data.total_sentences || 0,
          translatedSentences: data.translated_sentences || 0,
          progress: data.progress || 0,
        });

        if (data.status === 'completed') {
          cleanupBatchPolling();
          setIsBatchProcessing(false);
          if (data.results) {
            setBatchResult({
              total_requested: data.total_chapters,
              processed: data.processed_chapters,
              succeeded: data.succeeded_chapters,
              failed: data.failed_chapters,
              target_language: data.target_language,
              model: data.model,
              max_concurrent_requests: data.max_concurrent_requests,
              results: data.results,
            });
          }
          setBatchTaskId('');
        } else if (data.status === 'failed') {
          cleanupBatchPolling();
          setIsBatchProcessing(false);
          setBatchError(`批量翻译失败：${data.error || '未知错误'}`);
          setBatchTaskId('');
        }
      } catch (err) {
        if (isCancelled) {
          return;
        }

        cleanupBatchPolling();
        setIsBatchProcessing(false);
        if (err?.response?.status === 404) {
          setBatchError('批量翻译任务不存在或已过期');
        } else if (err?.code === 'ECONNABORTED') {
          setBatchError('获取批量翻译进度超时，请稍后重试');
        } else {
          const message =
            err?.response?.data?.detail ||
            err?.message ||
            '获取批量翻译进度失败';
          setBatchError(`获取批量翻译进度失败：${message}`);
        }
        setBatchTaskId('');
      }
    };

    fetchBatchStatus();
    batchPollTimerRef.current = setInterval(fetchBatchStatus, POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      cleanupBatchPolling();
    };
  }, [batchTaskId]);

  const savedUrlValue = result?.saved_url || '';
  const resolvedSavedUrl = useMemo(() => {
    if (!savedUrlValue) {
      return '';
    }
    if (/^https?:\/\//.test(savedUrlValue)) {
      return savedUrlValue;
    }
    return backendBaseUrl ? `${backendBaseUrl}${savedUrlValue}` : savedUrlValue;
  }, [savedUrlValue, backendBaseUrl]);

  const handleCopyLink = async () => {
    if (!resolvedSavedUrl) {
      return;
    }

    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        setCopyStatus('浏览器不支持剪贴板复制');
        return;
      }

      await navigator.clipboard.writeText(resolvedSavedUrl);
      setCopyStatus('下载链接已复制');
    } catch (err) {
      setCopyStatus('复制失败，请手动复制');
    }
  };

  const openDocsUrl = backendBaseUrl
    ? `${backendBaseUrl}/docs#/openai-translate/start_translation_task_open_ai_translate__post`
    : '/docs';

  const progressPercentage = Math.round(Math.min(1, Math.max(0, progress)) * 100);
  const isBusy = isUploading || isProcessing;

  const statusLabelMap = {
    idle: '等待处理',
    queued: '已排队',
    in_progress: '翻译中',
    completed: '已完成',
    failed: '失败',
  };

  return (
    <div className={styles.translateTool}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>OpenAI JSON 翻译</h1>
          <p className={styles.subtitle}>
            上传包含中文内容的 JSON 文件，自动调用 OpenAI 翻译为英文并保留原有结构。
          </p>
        </div>
        <a
          className={styles.docsLink}
          href={openDocsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          接口文档
        </a>
      </header>

      <section className={styles.panel}>
        <div className={styles.form}>
          <div>
            <h2 className={styles.panelTitle}>翻译设置</h2>
            <p className={styles.hint}>以下设置将同时应用于单文件翻译和批量章节翻译。</p>
          </div>

          <div className={styles.inlineFields}>
            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="targetLanguage">
                目标语言
              </label>
              <input
                id="targetLanguage"
                className={styles.input}
                type="text"
                value={targetLanguage}
                onChange={(event) => setTargetLanguage(event.target.value)}
              />
              <p className={styles.hint}>默认为 English，可根据需要调整。</p>
            </div>
            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="model">
                使用模型
              </label>
              <input
                id="model"
                className={styles.input}
                type="text"
                value={model}
                onChange={(event) => setModel(event.target.value)}
              />
              <p className={styles.hint}>
                默认 {DEFAULT_MODEL}，可根据账号权限调整例如 gpt-4o。
              </p>
            </div>
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="chunkSize">
              每次翻译句子数量
            </label>
            <input
              id="chunkSize"
              className={styles.input}
              type="number"
              min={1}
              max={200}
              value={chunkSize}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (Number.isNaN(value)) {
                  setChunkSize(DEFAULT_CHUNK_SIZE);
                  return;
                }
                const clamped = Math.min(200, Math.max(1, Math.round(value)));
                setChunkSize(clamped);
              }}
            />
            <p className={styles.hint}>
              建议 10-30 之间；值越大单批耗时越长，但总批次数越少。
            </p>
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="maxConcurrent">
              并发请求数量
            </label>
            <input
              id="maxConcurrent"
              className={styles.input}
              type="number"
              min={1}
              max={500}
              value={maxConcurrentRequests}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (Number.isNaN(value)) {
                  setMaxConcurrentRequests(DEFAULT_MAX_CONCURRENT_REQUESTS);
                  return;
                }
                const clamped = Math.min(500, Math.max(1, Math.round(value)));
                setMaxConcurrentRequests(clamped);
              }}
            />
            <p className={styles.hint}>
              控制同时向 OpenAI 发送的 chunk 数量，建议 1-5，过大可能触发限流。
            </p>
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="instructions">
              额外说明（可选）
            </label>
            <textarea
              id="instructions"
              className={styles.textarea}
              placeholder="例如：保持专有名词不翻译，数值保留原样等。"
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
            />
          </div>
        </div>
      </section>

      <section className={styles.panel}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div>
            <h2 className={styles.panelTitle}>上传 JSON 文件翻译</h2>
            <p className={styles.hint}>适用于单章或自定义 JSON 文件的翻译任务。</p>
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="jsonFile">
              JSON 文件
            </label>
            <input
              id="jsonFile"
              className={styles.fileInput}
              type="file"
              accept=".json,application/json"
              onChange={handleFileChange}
            />
            {selectedFile && (
              <p className={styles.fileInfo}>
                已选择：{selectedFile.name}（{formatFileSize(selectedFile.size)}）
              </p>
            )}
            <p className={styles.hint}>
              文件大小请控制在 8 MB 以内，需为 UTF-8 编码的合法 JSON。
            </p>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isBusy}
            >
              {isUploading ? '上传中...' : isProcessing ? '翻译进行中...' : '上传并翻译'}
            </button>
          </div>
        </form>
      </section>

      <section className={styles.panel}>
        <form className={styles.form} onSubmit={handleBatchSubmit}>
          <div>
            <h2 className={styles.panelTitle}>章节批量翻译</h2>
            <p className={styles.hint}>
              选择目标卷并勾选需要翻译的章节，系统将读取章节 JSON 并将译文写入对应的 content_trans。
            </p>
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="batchVolume">
              选择卷
            </label>
            <div className={styles.inlineFields}>
              <select
                id="batchVolume"
                className={styles.input}
                value={selectedVolumeId}
                onChange={(event) => setSelectedVolumeId(event.target.value)}
                disabled={volumesLoading || volumes.length === 0 || isBatchProcessing}
              >
                {volumes.length === 0 ? (
                  <option value="">暂无可用卷</option>
                ) : (
                  volumes.map((volume) => (
                    <option key={volume.id} value={volume.id}>
                      {volume.novel?.title ? `${volume.novel.title} / ` : ''}
                      {volume.title}
                    </option>
                  ))
                )}
              </select>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={loadVolumes}
                disabled={volumesLoading || isBatchProcessing}
              >
                {volumesLoading ? '加载中...' : '刷新卷列表'}
              </button>
            </div>
            {volumesError && <div className={styles.error}>{volumesError}</div>}
          </div>

          <div className={styles.formRow}>
            <div className={styles.listHeader}>
              <span>章节列表</span>
              <div className={styles.listActions}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={toggleSelectAllChapters}
                  disabled={chaptersLoading || chapters.length === 0 || isBatchProcessing}
                >
                  {chapters.length > 0 && selectedChapterIds.length === chapters.length
                    ? '取消全选'
                    : '全选'}
                </button>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => loadChapters(selectedVolumeId)}
                  disabled={chaptersLoading || !selectedVolumeId || isBatchProcessing}
                >
                  {chaptersLoading ? '刷新中...' : '刷新章节'}
                </button>
              </div>
            </div>
            {chaptersError && <div className={styles.error}>{chaptersError}</div>}
            {chaptersLoading ? (
              <p className={styles.hint}>章节加载中，请稍候…</p>
            ) : chapters.length === 0 ? (
              <p className={styles.hint}>当前卷暂无章节或尚未加载章节内容。</p>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.chapterTable}>
                  <thead>
                    <tr>
                      <th style={{ width: '3rem' }}>选择</th>
                      <th>顺序</th>
                      <th>章节标题</th>
                      <th>已翻译</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chapters.map((chapter) => {
                      const checked = selectedChapterIds.includes(chapter.id);
                      const hasTranslation = Boolean(chapter.content_trans && chapter.content_trans.trim());
                      return (
                        <tr key={chapter.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleChapterSelection(chapter.id)}
                              disabled={isBatchProcessing}
                            />
                          </td>
                          <td>{chapter.order_index ?? '-'}</td>
                          <td>{chapter.title}</td>
                          <td className={hasTranslation ? styles.statusUpdated : styles.statusPending}>
                            {hasTranslation ? '已存在' : '未生成'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {batchError && <div className={styles.error}>{batchError}</div>}

          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isBatchProcessing || selectedChapterIds.length === 0}
            >
              {isBatchProcessing ? '批量翻译中...' : '翻译选中章节'}
            </button>
          </div>
        </form>
      </section>

      <section className={styles.panel}>
        <div className={styles.reviewHeader}>
          <div>
            <h2 className={styles.panelTitle}>译文复审与单句修订</h2>
            <p className={styles.hint}>
              输入章节 ID 直接加载句子，支持逐句重新翻译或手动编辑后保存。
            </p>
          </div>
        </div>

        <div className={styles.formRow}>
          <label className={styles.label}>章节 ID</label>
          <div className={styles.inlineFields}>
            <input
              className={styles.input}
              type="number"
              min={1}
              placeholder="输入章节 ID，如 2680"
              value={directChapterIdInput}
              onChange={(event) => setDirectChapterIdInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleDirectChapterLoad(false);
                }
              }}
            />
            <button
              type="button"
              className={styles.submitBtn}
              onClick={() => handleDirectChapterLoad(false)}
              disabled={reviewSentencesLoading || !directChapterIdInput.trim()}
            >
              {reviewSentencesLoading && !chineseDetectMode ? '加载中…' : '加载句子'}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => handleDirectChapterLoad(true)}
              disabled={reviewSentencesLoading || !directChapterIdInput.trim()}
              title="检测译文中首字符为中文的异常句子"
            >
              {reviewSentencesLoading && chineseDetectMode ? '检测中…' : '检测中文'}
            </button>
          </div>
        </div>

        {reviewSelectedChapterId != null && (
          <>
            <div className={styles.reviewControls}>
              <div className={styles.reviewSummary}>
                <span>章节 ID：{reviewSelectedChapterId}</span>
                {reviewSelectedChapterTitle && (
                  <span style={{ marginLeft: '0.75rem', color: 'var(--gray-60, #6b7280)' }}>
                    {reviewSelectedChapterTitle}
                  </span>
                )}
                {reviewSentencesLoading ? (
                  <span>句子加载中…</span>
                ) : (
                  <span>共 {reviewSentences.length} 句</span>
                )}
                {chineseDetectMode && !reviewSentencesLoading && (
                  <span style={{
                    marginLeft: '0.75rem',
                    padding: '0.15rem 0.6rem',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    background: filteredReviewSentences.length > 0 ? '#fef2f2' : '#f0fdf4',
                    color: filteredReviewSentences.length > 0 ? '#dc2626' : '#16a34a',
                    border: `1px solid ${filteredReviewSentences.length > 0 ? '#fecaca' : '#bbf7d0'}`,
                  }}>
                    {filteredReviewSentences.length > 0
                      ? `检测到 ${filteredReviewSentences.length} 句译文首字符为中文`
                      : '✓ 未检测到异常'}
                  </span>
                )}
              </div>
              <div className={styles.reviewControlFields}>
                <input
                  className={styles.input}
                  placeholder="搜索原文或译文…"
                  value={sentenceFilter}
                  onChange={(event) => {
                    setSentenceFilter(event.target.value);
                    setEditingSentenceKey(null);
                  }}
                />
                <textarea
                  className={styles.reviewInstructionArea}
                  placeholder="可选：为重新翻译添加额外说明，示例：保持数字不变，专有名词请沿用原文。"
                  value={reviewInstructions}
                  onChange={(event) => setReviewInstructions(event.target.value)}
                />
                <div className={styles.reviewControlButtons}>
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={() => fetchReviewSentences(reviewSelectedChapterId)}
                    disabled={reviewSentencesLoading}
                  >
                    {reviewSentencesLoading ? '刷新中...' : '刷新句子'}
                  </button>
                </div>
              </div>
            </div>

            {reviewSentencesError && (
              <div className={styles.error}>{reviewSentencesError}</div>
            )}

            {reviewSentencesLoading ? (
              <p className={styles.hint}>正在加载句子，请稍候…</p>
            ) : reviewSentences.length === 0 ? (
              <p className={styles.hint}>该章节暂无句子数据。</p>
            ) : filteredReviewSentences.length === 0 ? (
              <p className={styles.hint}>
                {chineseDetectMode
                  ? `所有 ${reviewSentences.length} 句译文均正常，未检测到首字符为中文的句子。`
                  : `未找到匹配的句子（共 ${reviewSentences.length} 句）。`}
                {chineseDetectMode ? (
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}
                    onClick={() => setChineseDetectMode(false)}
                  >
                    查看全部句子
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}
                    onClick={() => setSentenceFilter('')}
                  >
                    清除筛选
                  </button>
                )}
              </p>
            ) : (
              <div className={styles.reviewSentenceList}>
                {filteredReviewSentences.map((sentence) => (
                  <div key={sentence.key} className={styles.reviewSentenceCard}>
                    <div className={styles.reviewSentenceHeader}>
                      <span className={styles.reviewSentenceIndex}>
                        #{sentence.index + 1}
                      </span>
                      {sentence.sentence_id != null && (
                        <span className={styles.reviewSentenceMeta}>
                          sentence_id: {sentence.sentence_id}
                        </span>
                      )}
                    </div>
                    <div className={styles.reviewSentenceOriginal}>
                      {sentence.original_text || <em>（原文为空）</em>}
                    </div>
                    <textarea
                      className={styles.reviewSentenceTextarea}
                      value={sentence.draft}
                      onChange={(event) =>
                        handleSentenceDraftChange(sentence.key, event.target.value)
                      }
                      onFocus={() => setEditingSentenceKey(sentence.key)}
                      placeholder="请输入该句的目标语言翻译"
                    />
                    {sentence.actionError && (
                      <div className={styles.error}>{sentence.actionError}</div>
                    )}
                    {sentence.actionSuccess && (
                      <div className={styles.success}>{sentence.actionSuccess}</div>
                    )}
                    <div className={styles.reviewSentenceActions}>
                      <button
                        type="button"
                        className={styles.secondaryBtn}
                        onClick={() => handleRetranslateSentence(sentence.key)}
                        disabled={sentence.isRetranslating}
                      >
                        {sentence.isRetranslating ? '重新翻译中…' : '重新翻译'}
                      </button>
                      <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={() => handleSaveSentenceTranslation(sentence.key)}
                        disabled={sentence.isSaving}
                      >
                        {sentence.isSaving ? '保存中…' : '保存更新'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {(isProcessing || status === 'in_progress' || status === 'queued') && (
        <section className={styles.progressSection}>
          <div className={styles.progressHeader}>
            <span className={styles.statusBadge}>{statusLabelMap[status] || status}</span>
            <span className={styles.progressDetail}>
              {completedChunks}/{totalChunks} 批次 • {translatedCount}/{totalSentences} 句
            </span>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className={styles.progressFooter}>
            <span>{progressPercentage}%</span>
            {promptTokens !== null && completionTokens !== null && (
              <span>
                Tokens: prompt {promptTokens} • completion {completionTokens}
              </span>
            )}
          </div>
        </section>
      )}

      {result && (
        <section className={styles.resultSection}>
          <div className={styles.resultHeader}>
            <div>
              <h2 className={styles.resultTitle}>翻译完成</h2>
              <p className={styles.resultSummary}>
                使用模型 {result.model || model} • 目标语言 {result.target_language || targetLanguage} • Tokens: prompt{' '}
                {result.prompt_tokens ?? '未知'} / completion {result.completion_tokens ?? '未知'}
              </p>
            </div>
            <div className={styles.resultActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={handleCopyLink}
                disabled={!resolvedSavedUrl}
              >
                复制下载链接
              </button>
              {resolvedSavedUrl && (
                <a
                  className={styles.secondaryBtn}
                  href={resolvedSavedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  打开文件
                </a>
              )}
            </div>
          </div>

          {copyStatus && (
            <div className={styles.copyStatus}>{copyStatus}</div>
          )}

          <div className={styles.metadata}>
            {result.saved_path && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>保存路径</span>
                <span className={styles.metaValue}>{result.saved_path}</span>
              </div>
            )}
            {resolvedSavedUrl && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>下载链接</span>
                <a
                  className={styles.metaLink}
                  href={resolvedSavedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {resolvedSavedUrl}
                </a>
              </div>
            )}
            {result.max_concurrent_requests && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>并发请求</span>
                <span className={styles.metaValue}>{result.max_concurrent_requests}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {(isBatchProcessing || batchStatus === 'in_progress' || batchStatus === 'queued') && (
        <section className={styles.progressSection}>
          <div className={styles.progressHeader}>
            <span className={styles.statusBadge}>
              {batchStatus === 'queued' ? '排队中' : batchStatus === 'in_progress' ? '翻译中' : '处理中'}
            </span>
            <span className={styles.progressDetail}>
              章节：{batchProgress.processedChapters}/{batchProgress.totalChapters} • 
              成功：{batchProgress.succeededChapters} • 
              失败：{batchProgress.failedChapters}
            </span>
          </div>
          {batchProgress.currentChapterTitle && (
            <div className={styles.progressDetail} style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              当前章节：{batchProgress.currentChapterTitle}
            </div>
          )}
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${Math.round(batchProgress.progress * 100)}%` }}
            />
          </div>
          <div className={styles.progressFooter}>
            <span>{Math.round(batchProgress.progress * 100)}%</span>
            <span>
              已翻译句子：{batchProgress.translatedSentences} / {batchProgress.totalSentences}
            </span>
          </div>
        </section>
      )}

      {batchResult && (
        <section className={styles.resultSection}>
          <div className={styles.resultHeader}>
            <div>
              <h2 className={styles.resultTitle}>批量翻译结果</h2>
              <p className={styles.resultSummary}>
                成功 {batchResult.succeeded} / {batchResult.total_requested} • 失败 {batchResult.failed}
              </p>
            </div>
          </div>

          <div className={styles.metadata}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>模型</span>
              <span className={styles.metaValue}>{batchResult.model}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>目标语言</span>
              <span className={styles.metaValue}>{batchResult.target_language}</span>
            </div>
            {batchResult.max_concurrent_requests && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>并发请求</span>
                <span className={styles.metaValue}>{batchResult.max_concurrent_requests}</span>
              </div>
            )}
          </div>

          {Array.isArray(batchResult.results) && batchResult.results.length > 0 && (
            <div className={styles.tableWrapper}>
              <table className={styles.chapterTable}>
                <thead>
                  <tr>
                    <th>章节 ID</th>
                    <th>标题</th>
                    <th>句子数</th>
                    <th>状态</th>
                    <th>详情</th>
                  </tr>
                </thead>
                <tbody>
                  {batchResult.results.map((item) => (
                    <tr key={item.chapter_id}>
                      <td>{item.chapter_id}</td>
                      <td>{item.title || '-'}</td>
                      <td>{typeof item.sentence_count === 'number' ? item.sentence_count : '-'}</td>
                      <td
                        className={
                          item.status === 'success'
                            ? styles.statusUpdated
                            : styles.statusFailed
                        }
                      >
                        {item.status}
                      </td>
                      <td>{item.detail || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default OpenAITranslateTool;
